import React, { useId, useState } from 'react';
import HarbourIcon from './HarbourIcon';
import './harbour.css';
import './harbourPasswordToggle.css';

/*
 * Harbour/Field: visible label always (never placeholder-only), hint under
 * the label, inline error with icon + helpful message. Default is the
 * canvas-grey fill; focus goes white with the 2px focus border; error shows
 * the 2px error border plus the error line under the box.
 *
 * `reveal` adds the show/hide-password control from the sign-in designs.
 */
export function HarbourField({
  label,
  hint,
  error,
  type = 'text',
  as = 'input',
  value,
  onChange,
  placeholder,
  className = '',
  inputClassName = '',
  reveal = false,
  children,
  ...rest
}) {
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const sharedProps = {
    id,
    value: value ?? '',
    onChange,
    placeholder,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    className: `harbour-input ${error ? 'harbour-input--error' : ''} ${reveal ? 'harbour-input--reveal' : ''} ${inputClassName}`.trim(),
    ...rest,
  };
  return (
    <div className={`harbour-field ${className}`.trim()}>
      <label htmlFor={id} className="harbour-field__label">{label}</label>
      {hint && <p id={hintId} className="harbour-field__hint">{hint}</p>}
      {as === 'select' ? (
        <select {...sharedProps}>{children}</select>
      ) : as === 'textarea' ? (
        <textarea {...sharedProps} />
      ) : reveal ? (
        <div className="harbour-reveal-wrap">
          <input type={revealed ? 'text' : 'password'} {...sharedProps} />
          <button
            type="button"
            className="harbour-reveal-btn"
            onClick={() => setRevealed((shown) => !shown)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
          >
            <HarbourIcon name="eye" size={20} />
          </button>
        </div>
      ) : (
        <input type={type} {...sharedProps} />
      )}
      {error && (
        <p id={errorId} className="harbour-field__error">
          <HarbourIcon name="error" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}

/*
 * Harbour/Date field: Day, Month and Year as three short fields under one
 * label; an error outlines all three and shows one error line beneath them.
 */
export function HarbourDateField({ label = 'Date of birth', hint, value = {}, onChange, error }) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const set = (part) => (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    onChange({ ...value, [part]: digits });
  };
  const inputClass = `harbour-input ${error ? 'harbour-input--error' : ''}`;
  return (
    <div className="harbour-field" role="group" aria-labelledby={`${id}-label`}>
      <span id={`${id}-label`} className="harbour-field__label">{label}</span>
      {hint && <p id={hintId} className="harbour-field__hint">{hint}</p>}
      <div className="harbour-date-field">
        <div className="harbour-field">
          <label htmlFor={`${id}-day`} className="harbour-field__label">Day</label>
          <input
            id={`${id}-day`}
            inputMode="numeric"
            maxLength={2}
            placeholder="DD"
            value={value.day || ''}
            onChange={set('day')}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            className={`${inputClass} harbour-input--day`}
          />
        </div>
        <div className="harbour-field">
          <label htmlFor={`${id}-month`} className="harbour-field__label">Month</label>
          <input
            id={`${id}-month`}
            inputMode="numeric"
            maxLength={2}
            placeholder="MM"
            value={value.month || ''}
            onChange={set('month')}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            className={`${inputClass} harbour-input--month`}
          />
        </div>
        <div className="harbour-field">
          <label htmlFor={`${id}-year`} className="harbour-field__label">Year</label>
          <input
            id={`${id}-year`}
            inputMode="numeric"
            maxLength={4}
            placeholder="YYYY"
            value={value.year || ''}
            onChange={set('year')}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            className={`${inputClass} harbour-input--year`}
          />
        </div>
      </div>
      {error && (
        <p id={errorId} className="harbour-field__error">
          <HarbourIcon name="error" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}

/** Join a date-field value into YYYY-MM-DD, or null while incomplete/invalid. */
export function dateFieldToIso({ day, month, year } = {}) {
  if (!day || !month || !year || String(year).length !== 4) return null;
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (!d || !m || d > 31 || m > 12) return null;
  const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime()) || parsed.getUTCDate() !== d) return null;
  return iso;
}
